/**
 * Comparison Sales Exception Configs
 *
 * Covers every cross-system LS<->Xero sale-mismatch code through one
 * compare table:
 * - ex-sale-ls-xero-status-mismatch       (ONACCOUNT/ONACCOUNT_CLOSED only)
 * - ex-sale-ls-xero-voided-status-mismatch (voided-state drift, weight 7)
 * - ex-sale-ls-xero-total-mismatch
 * - ex-sale-ls-xero-mismatch              (base - any mismatch true; weight 0)
 *
 * Inspector renders each sale as two TableRows (LS and Xero) via the
 * `rowSplit` opt-in on DataViewTable. The Issues chip spans both
 * rows with rowSpan=2; per-system cells (Date, Client, Sale#, Status,
 * line counts, amounts, payments) call `splitRender(row, system)`.
 * Sale# carries the LS receipt link on the LS row and the Xero
 * reference link on the Xero row, so the row's system is unambiguous
 * without a dedicated icon column. Mismatched Xero cells take red
 * text; numeric/status cells pin a signed delta chip below the value.
 *
 * Platform uses simplified flat columns + "View in Inspector" link.
 */
import React from 'react'
import { Typography, Box, Chip, Tooltip } from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import EventIcon from '@mui/icons-material/Event'
import PersonIcon from '@mui/icons-material/Person'
import RuleIcon from '@mui/icons-material/Rule'
import ListAltIcon from '@mui/icons-material/ListAlt'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import PaymentIcon from '@mui/icons-material/Payment'
import inspectorApi from '../../../services/inspector/api'
import { renderSaleLsLink, renderSaleXeroLink } from './saleLinkCells'
import {
  formatCurrencyAmount,
  formatAmountDisplay,
  formatClientName,
  xeroPaymentsForMatch,
  xeroSettledTotal,
} from '../../../utils/salesFormatters'
import { renderDeltaChip as renderCompareDeltaChip } from '../../../components/inspector/shared/compareSalesPalette'
import { NUMERIC_FONT_SX } from '../../../theme/numericFont'
import { formatBusinessDate } from '../../../utils/inspectorFormatters'
import { statusDisplay, expectedXeroStatus, SaleStatusDisplay } from '../../../utils/saleStatusDisplay'
import {
  composeRowSubtitle,
  datePart,
  moneyPart,
  textPart,
} from '../shared/rowSubtitle'
import {
  collectSaleMismatches,
  SaleMismatch,
  SaleMismatchInput,
} from '../../../utils/saleMismatchIssues'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import type {
  ExceptionConfig,
  ExceptionColumnDef,
  ColumnGroupDef,
  ExceptionFilterStripDef,
} from '../types'
import { MISMATCH_ROW_BG } from '../../../theme/exceptionTableStyles'
import { sharedSalesFilters, buildSalesFilterParams } from './sharedSalesFilters'

// ── Helpers ───────────────────────────────────────────────────────────

/**
 * Base sales-compare exception code. Single source of truth for the
 * code that drives the inspector compare view + mismatch chip strip.
 * Imported by `AttentionPanel` and `DynamicExceptionRoute` so the
 * dashboard CTA and the route's mismatch handling stay in lock-step.
 */
export const SALES_COMPARE_BASE_CODE = 'ex-sale-ls-xero-mismatch'

const MISMATCH_BG = MISMATCH_ROW_BG

/**
 * Build the canonical mismatch input from a row. Centralises the
 * field-name mapping so anything that needs the issues list reads
 * from the same place.
 *
 * The list endpoint projects sale_alignment's CTE booleans
 * (sale_statuses_match, sale_amounts_match, sale_clients_match, ...)
 * onto every row; we forward them so `collectSaleMismatches` skips
 * its local *Agree fallback and trusts the canonical SQL verdict.
 */
export const rowToMismatchInput = (row: Record<string, any>): SaleMismatchInput => ({
  ls: {
    date: row.sale_date,
    client: formatClientName(row),
    status: row.ls_status,
    amount_inc_tax: row.ls_total_amount,
    amount_ex_tax: row.ls_amount_ex_tax,
    tax: row.ls_total_tax,
    payments_total: row.payments_total,
  },
  xero: {
    date: row.xero_date,
    client: row.xero_contact_name,
    status: row.xero_status,
    amount_inc_tax: row.xero_total_amount,
    amount_ex_tax: row.xero_amount_ex_tax,
    tax: row.xero_total_tax,
    payments_total: xeroPaymentsForMatch(
      row.payments_total,
      row.xero_payments_total,
      row.xero_amount_credited,
      row.has_xero_credit,
    ),
  },
  // Backend populates sale_line_items_match for the base code only
  // (the line-pass runs there). Treat the row as mismatched if either
  // (a) the line-pass flagged errors OR (b) the raw counts differ. The
  // count guard catches cases where compare_line_items matches items
  // many-to-one and reports "no errors" while LS and Xero clearly
  // disagree on row count - without it, the Xero count cell stays
  // unhighlighted on rows like INV-GRV-38649 / INV-GRV-38376. Falls
  // through to null only when neither signal claims either way.
  lineItemsMismatch:
    row.sale_line_items_match === false
    || (row.ls_line_count != null
        && row.xero_line_count != null
        && row.ls_line_count !== row.xero_line_count)
      ? true
      : row.sale_line_items_match === true
        ? false
        : null,
  lsLineCount: row.ls_line_count,
  xeroLineCount: row.xero_line_count,
  statusesMatch: row.sale_statuses_match,
  amountsExTaxMatch: row.sale_amounts_ex_tax_match,
  amountsIncTaxMatch: row.sale_amounts_match,
  taxMatch: row.sale_tax_match,
  paymentsMatch: row.sale_payments_match,
  clientsMatch: row.sale_clients_match,
  datesMatch: row.sale_dates_match,
})

const collectIssuesForRow = (row: Record<string, any>): SaleMismatch[] =>
  collectSaleMismatches(rowToMismatchInput(row))

/**
 * True when this row has a mismatch on the given key (one of the keys
 * in SALE_MISMATCH_KEYS). Drives the red text wash on Xero cells and
 * ensures the Issues chip count agrees with what's visually flagged
 * on each row.
 */
const isRowMismatchedOn = (row: Record<string, any>, mismatchKey: string): boolean =>
  collectIssuesForRow(row).some(issue => issue.key === mismatchKey)

/**
 * Backend-boolean cell-style helper retained for the platform-context
 * columns, which render through a different table that doesn't share
 * the inspector's full row shape.
 */
const platformMismatchSx = (matchField: string) => (_value: any, row: Record<string, any>): Record<string, any> =>
  row[matchField] === false ? { backgroundColor: MISMATCH_BG } : {}

const MISMATCH_TEXT = '#EC1C5A'

// ── Inspector cell renderers ──────────────────────────────────────────
//
// The inspector view runs the table with `rowSplit: { systems: ['lightspeed',
// 'xero'] }`. Most columns expose a `splitRender(row, system)` callback:
// the table calls it once per system row, so each value lives in its own
// `<TableRow>` and gets independent hover + per-row height. Only the
// Issues column (which is a per-sale aggregate) uses `spansSystems` with
// rowSpan=2.

/**
 * Apply red text on Xero rows that mismatch the given key. Wraps the
 * cell content in a Box so MUI Typography children inherit the colour.
 * `display: 'block'` lets the delta chip below sit on its own line and
 * inherit the cell's text-align (right for amounts, center for status,
 * etc.) without per-cell alignment plumbing.
 */
const xeroMismatchBox = (
  system: 'lightspeed' | 'xero',
  mismatchKey: string,
  row: Record<string, any>,
  child: React.ReactNode,
): React.ReactNode => {
  const isMismatched = system === 'xero' && isRowMismatchedOn(row, mismatchKey)
  return React.createElement(
    Box,
    {
      sx: {
        color: isMismatched ? MISMATCH_TEXT : 'inherit',
        display: 'block',
      },
    },
    child,
  )
}

/**
 * Signed delta chip for the Xero row of a compare cell. Delegates to
 * the shared compare-sales palette so the inspector table and the
 * side-by-side dialog render identical chips (red for Xero<LS, green
 * for Xero>LS, arrow icon, soft tint). LS row never gets a chip - the
 * delta only makes sense once relative to the source-of-truth.
 *
 * Currency (`isCurrency=true`) compares ABS magnitudes so credit-note
 * pairs (LS negative, Xero positive) read by magnitude; int falls back
 * to a raw diff for line / payment counts. Returns null on the LS side
 * or when within rounding so callers can branch cleanly.
 */
const renderXeroDelta = (
  ls: number | null | undefined,
  xero: number | null | undefined,
  isCurrency: boolean,
  system: 'lightspeed' | 'xero',
): React.ReactNode => {
  if (system === 'lightspeed' || ls == null || xero == null) return null
  const diff = isCurrency ? Math.abs(xero) - Math.abs(ls) : (xero || 0) - (ls || 0)
  const chip = renderCompareDeltaChip(diff, isCurrency)
  if (!chip) return null
  // Wrap in a block-level Box so the chip lands on its own line under
  // the value, inheriting the cell's text-align (right for amounts,
  // center for counts) without per-call alignment plumbing.
  return React.createElement(
    Box,
    { sx: { mt: 0.25, display: 'block', textAlign: 'inherit' } },
    chip,
  )
}

/**
 * Sale # cell - LS row shows the LS receipt; Xero row shows the Xero
 * reference (or '-' when missing). Both via the shared sale-link renderers
 * (`saleLinkCells`), the single source for the row -> InvoiceLinkCell mapping.
 */
const renderSaleNumber = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode =>
  system === 'lightspeed' ? renderSaleLsLink(row) : renderSaleXeroLink(row)

const renderDate = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode => {
  const raw = system === 'lightspeed' ? row.sale_date : row.xero_date
  const text = raw ? formatBusinessDate(raw) : '-'
  const node = React.createElement(
    Typography,
    { variant: 'body2', sx: { fontSize: '0.875rem', whiteSpace: 'nowrap', color: 'inherit' } },
    text,
  )
  return xeroMismatchBox(system, 'sale_date', row, node)
}

/**
 * 2-line client-name clamp with hover tooltip showing the full name.
 * Long client names were making rows uneven; clamp keeps the table
 * scannable.
 *
 * Block-level container + ``maxWidth: 100%`` so the Webkit line-clamp
 * has a measured width to clamp against. The inline ``span`` shape
 * we previously used ignored width constraints, letting names like
 * "Stockwell Property Management Dutton Place Appartments B/c" push
 * the row wider than the 160px Client column.
 */
const clientClampSx = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '0.875rem',
  lineHeight: 1.3,
  wordBreak: 'break-word' as const,
  maxWidth: '100%',
  minWidth: 0,
}

const renderClientName = (name: string): React.ReactNode => {
  const display = name || '-'
  const clamped = React.createElement(
    Box,
    { component: 'div', sx: { ...clientClampSx, color: 'inherit' } },
    display,
  )
  if (!name) return clamped
  return withTooltip(
    { title: display, arrow: true, placement: 'top-start', enterDelay: 300 },
    clamped,
  )
}

const renderClient = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode => {
  const name = system === 'lightspeed'
    ? (formatClientName(row) || '')
    : (row.xero_contact_name || '')
  return xeroMismatchBox(system, 'client', row, renderClientName(name))
}

const STATUS_CHIP_SX = {
  height: 26,
  fontSize: '0.78rem',
  fontWeight: 600,
  // Outlined variant gets a subtle 1.5px border so the colour reads
  // clearly without the chip becoming a coloured slab.
  borderWidth: 1.5,
}

/**
 * Status chip used by the inspector table's Status column. When the
 * caller passes an `expectedLabel` (Xero side only, and only when the
 * actual Xero status diverges from the LS-implied expectation), an
 * amber ⚠ is embedded *inside* the chip on the trailing edge — same
 * "label-plus-icon" pattern as the side-by-side compare popup, with
 * the icon on the right rather than the left so it reads as a
 * post-fix annotation rather than a chip leading icon.
 */
const renderStatusChip = (
  display: SaleStatusDisplay,
  expectedLabel?: string | null,
): React.ReactNode => {
  const showWarning = !!expectedLabel
  const labelNode = showWarning
    ? React.createElement(
        Box,
        {
          component: 'span',
          sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 },
        },
        display.label,
        React.createElement(WarningAmberIcon, {
          sx: { fontSize: 14, color: '#ED6C02' },
        }),
      )
    : display.label
  const chip = React.createElement(Chip, {
    label: labelNode,
    size: 'small',
    color: display.color === 'default' ? 'default' : display.color,
    variant: 'outlined',
    sx: STATUS_CHIP_SX,
  })
  if (!showWarning) return chip
  // Wrap in a span so the Tooltip ref target is a DOM node - MUI's
  // Tooltip can't anchor directly to a Chip without one, and `cursor:
  // help` signals the chip is interactive even though it's read-only.
  return withTooltip(
    { title: `Expected: ${expectedLabel}`, arrow: true, placement: 'top' },
    React.createElement(
      Box,
      { component: 'span', sx: { display: 'inline-flex', cursor: 'help' } },
      chip,
    ),
  )
}

const renderStatus = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode => {
  const raw = system === 'lightspeed' ? row.ls_status : row.xero_status
  const display = statusDisplay(system, raw)
  // Only the Xero chip carries the "expected" warning - LS is the
  // source of truth, so showing a warning there would be circular.
  let expectedLabel: string | null = null
  if (system === 'xero') {
    const expected = expectedXeroStatus(row.ls_status)
    const actual = statusDisplay('xero', row.xero_status)
    if (expected && (!actual || actual.label !== expected.label)) {
      expectedLabel = expected.label
    }
  }
  const valueNode = display
    ? renderStatusChip(display, expectedLabel)
    : React.createElement(
        Typography,
        { variant: 'body2', sx: { color: 'text.disabled' } },
        '-',
      )
  return xeroMismatchBox(system, 'status', row, valueNode)
}

const monoTextSx = {
  // Normal weight is the default for numbers. The old monospace face had
  // no medium cut so a declared 500 rendered as 400; Inter has a real 500,
  // so anything above 400 here would read bold across EVERY value cell.
  // Only the inc-tax Total (renderAmountIncTax) opts into a heavier weight.
  fontWeight: 400,
  fontSize: '0.9625rem',
  // Canonical numeric type (Inter + tabular-nums) - matches the Compare
  // Sales modal and the report tables so amounts read identically.
  ...NUMERIC_FONT_SX,
  color: 'inherit',
  lineHeight: 1.4,
}

const monoSpan = (text: string | number, extraSx?: Record<string, any>): React.ReactNode =>
  React.createElement(
    Box,
    {
      component: 'span',
      sx: extraSx ? { ...monoTextSx, ...extraSx } : monoTextSx,
    },
    text,
  )

// Subtotal / Tax sit below the inc-tax Total in the hierarchy: normal
// weight so only the headline Total (and the red mismatch wash) carry
// visual emphasis.
const renderAmountExTax = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode => {
  const value = system === 'lightspeed' ? row.ls_amount_ex_tax : row.xero_amount_ex_tax
  const node = monoSpan(formatAmountDisplay(value), { fontWeight: 400 })
  const delta = renderXeroDelta(row.ls_amount_ex_tax, row.xero_amount_ex_tax, true, system)
  return xeroMismatchBox(system, 'ls_amount_ex_tax', row, React.createElement(React.Fragment, null, node, delta))
}

const renderTax = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode => {
  const value = system === 'lightspeed' ? row.ls_total_tax : row.xero_total_tax
  const node = monoSpan(formatAmountDisplay(value), { fontWeight: 400 })
  const delta = renderXeroDelta(row.ls_total_tax, row.xero_total_tax, true, system)
  return xeroMismatchBox(system, 'ls_total_tax', row, React.createElement(React.Fragment, null, node, delta))
}

const renderAmountIncTax = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode => {
  const value = system === 'lightspeed' ? row.ls_total_amount : row.xero_total_amount
  const text = system === 'lightspeed' ? formatCurrencyAmount(value) : formatAmountDisplay(value)
  // Inc-tax Total nudges weight + size up from monoTextSx so the
  // headline number reads heavier than Subtotal / Tax. Colour set to
  // #444 to soften the visual blackness, but only when the cell isn't
  // about to be washed red by a Xero mismatch (in which case we let
  // xeroMismatchBox drive colour via inheritance).
  const isXeroMismatch = system === 'xero' && isRowMismatchedOn(row, 'ls_total_amount')
  const node = monoSpan(text, {
    fontWeight: 600,
    fontSize: '1rem',
    ...(isXeroMismatch ? {} : { color: '#444444' }),
  })
  const delta = renderXeroDelta(row.ls_total_amount, row.xero_total_amount, true, system)
  return xeroMismatchBox(system, 'ls_total_amount', row, React.createElement(React.Fragment, null, node, delta))
}

const renderPayments = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode => {
  // Xero side rolls credit-note allocations into the Payments cell
  // whenever has_xero_credit is true: amount = paid + credited, count =
  // payments + allocations. Mirrors the canonical xero_settled_total in
  // sale_alignment's CTE so the headline figure tells the analyst what
  // Xero considers settled in total. Chip-strip + Issues count stay
  // credit-aware (mirrored in rowToMismatchInput) so a credit-reconciled
  // row visually differs from LS but does NOT show as a payments
  // mismatch - the credit explains the gap.
  let amount: number | null | undefined
  let count: number | null | undefined
  if (system === 'lightspeed') {
    amount = row.payments_total
    count = row.payment_count
  } else if (row.has_xero_credit) {
    amount = row.xero_settled_total ?? xeroSettledTotal(row.xero_payments_total, row.xero_amount_credited)
    count = (row.xero_payment_count ?? 0) + (row.xero_credit_count ?? 0)
  } else {
    amount = row.xero_payments_total
    count = row.xero_payment_count
  }
  // Delta chip uses whichever Xero figure agrees with LS under the
  // credit-aware OR so a credit-reconciled cell shows no stale delta
  // even when the displayed amount differs from LS. Same helper that
  // rowToMismatchInput uses, so the chip and the verdict stay in step.
  const xeroDeltaBase = xeroPaymentsForMatch(
    row.payments_total,
    row.xero_payments_total,
    row.xero_amount_credited,
    row.has_xero_credit,
  )
  const amountNode = monoSpan(amount != null ? formatCurrencyAmount(amount) : '-')
  const countNode = count != null && count > 0
    ? monoSpan(`(${count})`, { fontWeight: 400, ml: 0.5 })
    : null
  const delta = renderXeroDelta(row.payments_total, xeroDeltaBase, true, system)
  return xeroMismatchBox(
    system,
    'payments_total',
    row,
    React.createElement(React.Fragment, null, amountNode, countNode, delta),
  )
}

const renderLineItemCount = (row: Record<string, any>, system: 'lightspeed' | 'xero'): React.ReactNode => {
  const value = system === 'lightspeed' ? row.ls_line_count : row.xero_line_count
  const node = monoSpan(value != null ? value : '-')
  const delta = renderXeroDelta(row.ls_line_count, row.xero_line_count, false, system)
  // The count cell paints red only when the raw counts disagree -
  // line *content* mismatches (price / item swaps) live in the Issues
  // chip, not here, so a sale with 3 lines on each side that differ
  // on price doesn't read as a "count mismatch" at the cell level.
  const countsDiffer =
    row.ls_line_count != null
    && row.xero_line_count != null
    && row.ls_line_count !== row.xero_line_count
  const isMismatched = system === 'xero' && countsDiffer
  return React.createElement(
    Box,
    {
      sx: {
        color: isMismatched ? MISMATCH_TEXT : 'inherit',
        display: 'block',
      },
    },
    React.createElement(React.Fragment, null, node, delta),
  )
}

// ── Column definitions ────────────────────────────────────────────────

// Inspector column groups. Identity (Issues + Sale #) is frozen-left so
// the row's anchors stay visible while the wider amount columns scroll.
// The remaining groups walk left-to-right in the same order as the
// row's mismatches surface in `collectSaleMismatches`.
const COMPARE_SALES_INSPECTOR_GROUPS: ColumnGroupDef[] = [
  { id: 'identity', separatorAfter: true },
  { id: 'when_who', label: 'Sale Details', separatorAfter: true },
  { id: 'status', label: 'Status', separatorAfter: true },
  { id: 'amounts', label: 'Amounts', separatorAfter: true },
  { id: 'payments', label: 'Payments', separatorAfter: true },
]

// Fixed widths for the frozen identity columns. DataViewTable's
// freeze-offset math reads explicit width (not minWidth) so sticky
// columns don't slide under their neighbours on horizontal scroll;
// see frontend/src/config/exceptions/shared/productExceptionColumns.ts.
// Freeze runs all the way through the When/Who group: Issues anchors
// the row identity, Date / Client / Sale# stay pinned while the wider
// Status / Amounts / Payments columns scroll. Sale# carries its own
// per-system label (LS receipt vs Xero ref) so the row's system is
// visible without a dedicated icon column.
const FROZEN_IDENTITY_WIDTHS = {
  sale_date: 110,
  client: 160,
  invoice_number: 180,
} as const

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'sale_date',
    label: 'Date',
    align: 'left',
    contexts: ['inspector'],
    splitRender: renderDate,
    getSortValue: (row) => row.sale_date || '',
    group: 'when_who',
    freeze: 'left',
    width: FROZEN_IDENTITY_WIDTHS.sale_date,
  },
  {
    key: 'client',
    label: 'Client',
    align: 'left',
    contexts: ['inspector'],
    splitRender: renderClient,
    getSortValue: (row) => formatClientName(row),
    group: 'when_who',
    freeze: 'left',
    width: FROZEN_IDENTITY_WIDTHS.client,
  },
  {
    key: 'invoice_number',
    label: 'Sale #',
    align: 'left',
    contexts: ['inspector'],
    splitRender: renderSaleNumber,
    getSortValue: (row) => row.invoice_number || '',
    group: 'when_who',
    freeze: 'left',
    width: FROZEN_IDENTITY_WIDTHS.invoice_number,
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
    contexts: ['inspector'],
    splitRender: renderStatus,
    getSortValue: (row) => statusDisplay('lightspeed', row.ls_status)?.label || '',
    group: 'status',
  },
  {
    key: 'line_items',
    label: 'Line Items',
    align: 'center',
    contexts: ['inspector'],
    splitRender: renderLineItemCount,
    getSortValue: (row) => row.ls_line_count ?? 0,
    group: 'status',
  },
  {
    key: 'ls_amount_ex_tax',
    label: 'Total\n(ex. tax)',
    align: 'right',
    contexts: ['inspector'],
    splitRender: renderAmountExTax,
    getSortValue: (row) => row.ls_amount_ex_tax || 0,
    group: 'amounts',
  },
  {
    key: 'ls_total_tax',
    label: 'Tax',
    align: 'right',
    contexts: ['inspector'],
    splitRender: renderTax,
    getSortValue: (row) => row.ls_total_tax || 0,
    group: 'amounts',
  },
  {
    key: 'ls_total_amount',
    label: 'Total\n(inc. tax)',
    align: 'right',
    contexts: ['inspector'],
    splitRender: renderAmountIncTax,
    getSortValue: (row) => row.ls_total_amount,
    group: 'amounts',
  },
  {
    key: 'payments_total',
    label: 'Payments',
    align: 'right',
    contexts: ['inspector'],
    splitRender: renderPayments,
    getSortValue: (row) => row.payments_total || 0,
    group: 'payments',
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'invoice_number',
    label: 'LS Sale #',
    contexts: ['platform'],
    link: { field: 'lightspeed_sale_link', system: 'lightspeed' },
  },
  {
    key: 'sale_date',
    label: 'Sale Date',
    type: 'business_date',
    contexts: ['platform'],
    exportFormat: 'date',
  },
  {
    key: 'customer_name',
    label: 'Client',
    contexts: ['platform'],
  },
  {
    key: 'ls_total_amount',
    label: 'LS Total',
    type: 'currency',
    align: 'right',
    contexts: ['platform'],
    exportFormat: 'currency',
    getCellStyle: platformMismatchSx('sale_amounts_match'),
  },
  {
    key: 'xero_total_amount',
    label: 'Xero Total',
    type: 'currency',
    align: 'right',
    contexts: ['platform'],
    exportFormat: 'currency',
    getCellStyle: platformMismatchSx('sale_amounts_match'),
  },
  {
    key: 'ls_status',
    label: 'LS Status',
    contexts: ['platform'],
    getCellStyle: platformMismatchSx('sale_statuses_match'),
  },
  {
    key: 'xero_status',
    label: 'Xero Status',
    contexts: ['platform'],
    getCellStyle: platformMismatchSx('sale_statuses_match'),
  },
  {
    key: 'mismatch_type',
    label: 'Mismatch Type',
    contexts: ['platform'],
  },
  {
    key: 'ls_line_count',
    label: 'LS Lines',
    type: 'integer',
    align: 'right',
    contexts: ['platform'],
  },
  {
    key: 'xero_line_count',
    label: 'Xero Lines',
    type: 'integer',
    align: 'right',
    contexts: ['platform'],
  },
  {
    key: 'xero_reference',
    label: 'Xero Inv. #',
    contexts: ['platform'],
    link: { field: 'xero_invoice_link', system: 'xero' },
  },
  {
    key: 'exception_type',
    label: 'Exception Type',
    contexts: ['platform'],
  },
]

// ── Sales-compare page recipe ────────────────────────────────────────
//
// The sales-compare base code drives a "rollup" page that aggregates
// every cross-system mismatch axis. The exception type's name reads as
// one specific mismatch (e.g. "Sale status mismatch") which would
// mislead the broader view, so we override with a dedicated page
// header. The chip strip carries the active lens.
const SALES_COMPARE_TITLE = 'Sales comparison issues'
const SALES_COMPARE_DESCRIPTION =
  'Find sales where Lightspeed and Xero do not match, then filter by the part of the sale that needs review.'

// Valid `?mismatch=` URL values - the chip strip writes one of these.
// Anything else is normalised to no filter so a typo'd or stale URL
// still renders the unfiltered list.
const VALID_MISMATCHES = new Set<string>([
  'date',
  'client',
  'status',
  'status.voided',
  'status.paid',
  'lineItems',
  'amounts',
  'amounts.exTax',
  'amounts.tax',
  'amounts.incTax',
  'payments',
  'payments.lsZero',
  'payments.xeroZero',
  'payments.amountDiff',
  'payments.creditUnallocated',
])

// Page-size options offered on the server-paginated table. Anything
// bigger than 250 makes the per-row line-item compare on the backend
// (sales.py compare_line_items) feel slow again, defeating the reason
// we paginated in the first place.
const COMPARE_TABLE_PAGE_SIZE_OPTIONS = [25, 50, 100, 250]
const COMPARE_TABLE_DEFAULT_PAGE_SIZE = 50

// Mismatch chip-strip definition. Replaces the standalone
// `SaleMismatchFilter.tsx` - card visuals (icons, labels, sub-card
// breakdown groupings) live here so adding / renaming a mismatch axis
// is one config change.
// Read every valid mismatch value the URL currently carries (one or
// many - the Reason row is multi-select). Empty list = no filter.
const readMismatchValues = (params: URLSearchParams): string[] =>
  params.getAll('mismatch').filter((v) => VALID_MISMATCHES.has(v))

const compareSalesMismatchStrip: ExceptionFilterStripDef = {
  type: 'cards',
  buildCards: (_response, counts) => {
    const get = (key: string): number => (counts ? Number(counts[key] ?? 0) : 0)
    return [
      { key: null, label: 'All', count: get('all'), icon: <WarningAmberIcon /> },
      {
        key: 'status',
        label: 'Sale status',
        count: get('status'),
        icon: <RuleIcon />,
        subCards: {
          title: 'Status breakdown',
          cards: [
            { key: 'status.voided', label: 'Voided sales', count: get('statusVoided') },
            { key: 'status.paid', label: 'Paid in Xero', count: get('statusPaid') },
          ],
        },
      },
      { key: 'lineItems', label: 'Line items', count: get('lineItems'), icon: <ListAltIcon /> },
      {
        key: 'amounts',
        label: 'Amounts',
        count: get('amounts'),
        icon: <RequestQuoteIcon />,
        subCards: {
          title: 'Amounts breakdown',
          cards: [
            { key: 'amounts.exTax', label: 'Total (ex. tax)', count: get('amountsExTax') },
            { key: 'amounts.tax', label: 'Tax', count: get('amountsTax') },
            { key: 'amounts.incTax', label: 'Total (inc. tax)', count: get('amountsIncTax') },
          ],
        },
      },
      {
        key: 'payments',
        label: 'Payments',
        count: get('payments'),
        icon: <PaymentIcon />,
        subCards: {
          title: 'Payments breakdown',
          cards: [
            { key: 'payments.lsZero', label: 'LS not closed', count: get('paymentsLsZero') },
            { key: 'payments.xeroZero', label: 'Xero not reconciled', count: get('paymentsXeroZero') },
            { key: 'payments.amountDiff', label: 'Amount differs', count: get('paymentsAmountDiff') },
            {
              key: 'payments.creditUnallocated',
              label: 'Credit not applied',
              count: get('paymentsCreditUnallocated'),
            },
          ],
        },
      },
      { key: 'client', label: 'Client', count: get('client'), icon: <PersonIcon /> },
      { key: 'date', label: 'Date', count: get('date'), icon: <EventIcon /> },
    ]
  },
  // Parent key reflects the first valid mismatch value. Dotted values
  // (`amounts.tax`) collapse to their parent (`amounts`) so the parent
  // tile lights up; flat values (`lineItems`) stand alone. Empty URL =
  // no parent, no Reason row.
  getActiveKey: (params) => {
    const values = readMismatchValues(params)
    if (values.length === 0) return null
    const first = values[0]
    const dot = first.indexOf('.')
    return dot === -1 ? first : first.slice(0, dot)
  },
  // Single-axis click on a parent / leaf card. Replaces any
  // multi-select sub-keys; the Reason row will rebuild from the new
  // active parent.
  applySelection: (params, key) => {
    const next = new URLSearchParams(params)
    next.delete('mismatch')
    if (key) next.append('mismatch', key)
    return next
  },
  // Parent activates when any current mismatch value is one of its
  // sub-mismatches so clicking 'amounts.tax' lights up the 'amounts'
  // parent too.
  isCardActive: (cardKey, activeKey) => {
    if (cardKey === activeKey) return true
    if (!cardKey || typeof activeKey !== 'string') return false
    return activeKey.startsWith(`${cardKey}.`)
  },
  // Multi-select sub-axis: each chip in the Reason row is a dotted key
  // (e.g. `amounts.tax`). Empty selection collapses the URL to the
  // parent rollup (`?mismatch=amounts`) so the table shows every
  // sub-axis under that parent.
  getActiveSubKeys: (params) =>
    readMismatchValues(params).filter((v) => v.includes('.')),
  applySubSelection: (params, parentKey, subKeys) => {
    const next = new URLSearchParams(params)
    next.delete('mismatch')
    if (subKeys.length === 0) next.append('mismatch', parentKey)
    else for (const k of subKeys) next.append('mismatch', k)
    return next
  },
}

export const compareSalesConfig: ExceptionConfig = {
  codes: [
    'ex-sale-ls-xero-status-mismatch',
    'ex-sale-ls-xero-voided-status-mismatch',
    'ex-sale-ls-xero-total-mismatch',
    'ex-sale-ls-xero-mismatch',
  ],
  columns: [
    { ...buildIssuesColumn(), group: 'when_who' },
    ...inspectorColumns,
    ...platformColumns,
  ],
  columnGroups: COMPARE_SALES_INSPECTOR_GROUPS,
  defaultSortColumn: 'sale_date',
  defaultSortDirection: 'desc',
  complexity: 'comparison',
  rowKeyField: 'ls_id',
  getRowSubtitle: row =>
    composeRowSubtitle([
      textPart(row.customer_name) || 'Walk-in',
      datePart(row.sale_date),
      moneyPart(row.total_amount_incl_tax ?? row.ls_total_amount),
    ]),
  rowSplit: { systems: ['lightspeed', 'xero'] },
  rowDetail: {
    type: 'compare-sales',
    headerLabel: 'Actions',
  },
  filters: [
    {
      key: 'search',
      type: 'search',
      label: 'Search',
      placeholder: 'Search client, sale # or invoice',
    },
    ...sharedSalesFilters,
  ],
  pageHeader: {
    title: SALES_COMPARE_TITLE,
    description: SALES_COMPARE_DESCRIPTION,
    breadcrumb: SALES_COMPARE_TITLE,
  },
  serverTable: {
    defaultPageSize: COMPARE_TABLE_DEFAULT_PAGE_SIZE,
    pageSizeOptions: COMPARE_TABLE_PAGE_SIZE_OPTIONS,
    enableSearch: true,
  },
  filterStrips: [compareSalesMismatchStrip],
  fetchFilterCounts: (fpCode) =>
    inspectorApi.getSalesMismatchCounts(fpCode),
  buildFetchParams: (urlState) => {
    const params: Record<string, any> = {}
    const mismatches = urlState.searchParams
      .getAll('mismatch')
      .filter((v) => VALID_MISMATCHES.has(v))
    if (mismatches.length > 0) params.mismatch = mismatches
    return { ...params, ...buildSalesFilterParams(urlState.filterValues) }
  },
  // Legacy codes redirect to the base code with a `?mismatch=` preset.
  // Each preset matches the discrete tagger's actual scope so the
  // rendered count agrees with the card the user clicked:
  // 'ex-sale-ls-xero-total-mismatch' tags only inc-tax drift (narrower
  // than the broader 'amounts' rollup), so the redirect sends users to
  // the matching sub-axis.
  legacyRedirects: {
    'ex-sale-ls-xero-status-mismatch': {
      to: SALES_COMPARE_BASE_CODE,
      paramKey: 'mismatch',
      paramValue: 'status',
    },
    'ex-sale-ls-xero-voided-status-mismatch': {
      to: SALES_COMPARE_BASE_CODE,
      paramKey: 'mismatch',
      paramValue: 'status.voided',
    },
    'ex-sale-ls-xero-total-mismatch': {
      to: SALES_COMPARE_BASE_CODE,
      paramKey: 'mismatch',
      paramValue: 'amounts.incTax',
    },
  },
}
